<!-- ── Page ───────────────────────────────────────────────────────────── -->
<div class="wizard-page">

  <div class="wizard-header">
    <button mat-icon-button type="button" (click)="router.navigate(['/create'])" matTooltip="Back to Resource Creator">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h2 class="wizard-title">New User Account Template</h2>
  </div>

  <mat-card class="wizard-card">
    <mat-horizontal-stepper [linear]="true" #stepper>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 1 — Template Details                                      -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="templateDetailsForm">
        <ng-template matStepLabel>
          <span matTooltip="A descriptive name makes it easy to select the right account template when provisioning users for a class"
                matTooltipPosition="below">Template Details</span>
        </ng-template>

        <app-template-details-step [form]="templateDetailsForm"></app-template-details-step>

        <div class="step-actions">
          <span></span>
          <button mat-raised-button color="primary"
                  [disabled]="templateDetailsForm.invalid || templateDetailsForm.pending"
                  matStepperNext>
            Next
            <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 2 — User Account Configuration                           -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="userAccountForm">
        <ng-template matStepLabel>
          <span matTooltip="The access level determines what Azure resources provisioned users can manage — choose the minimum required for the course"
                matTooltipPosition="below">Account Configuration</span>
        </ng-template>

        <form [formGroup]="userAccountForm" class="step-form">

          <!-- ── Cloud Provider ── -->
          <div class="section-label">
            <mat-icon class="section-icon">cloud</mat-icon>
            <span>Cloud Provider</span>
          </div>
          <div class="provider-row">
            <div class="provider-tile provider-tile--selected">
              <img src="assets/images/resources/cloudprovider/azure.png" alt="Microsoft Azure"
                   class="provider-logo"
                   onerror="this.style.display='none'; this.nextElementSibling.style.display='inline-flex'" />
              <mat-icon class="provider-fallback-icon">cloud</mat-icon>
              <span class="provider-name">Microsoft Azure</span>
              <mat-icon class="provider-check">check_circle</mat-icon>
            </div>
            <span class="provider-note">Additional cloud providers will be available in a future release.</span>
          </div>

          <!-- ── Access Level ── -->
          <div class="section-label">
            <mat-icon class="section-icon">security</mat-icon>
            <span>Access Level <span class="required-asterisk">*</span></span>
          </div>

          <div class="access-level-grid">
            <div *ngFor="let level of accessLevels"
                 class="access-tile"
                 [class.access-tile--selected]="selectedAccessLevelId === level.id"
                 (click)="selectAccessLevel(level.id)"
                 [attr.role]="'radio'"
                 [attr.aria-checked]="selectedAccessLevelId === level.id">
              <mat-icon class="access-icon">{{ level.icon }}</mat-icon>
              <div class="access-info">
                <span class="access-name">{{ level.name }}</span>
                <span class="access-desc">{{ level.description }}</span>
                <span class="access-role" *ngIf="level.id !== ''">
                  Azure role: <strong>{{ azureRoles[level.id] }}</strong>
                </span>
              </div>
              <mat-icon class="access-check" *ngIf="selectedAccessLevelId === level.id">check_circle</mat-icon>
            </div>
          </div>

          <div class="field-error"
               *ngIf="userAccountForm.get('accessLevel')?.invalid && userAccountForm.get('accessLevel')?.touched">
            Please select an access level
          </div>

        </form>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious type="button">
            <mat-icon>arrow_back</mat-icon>
            Back
          </button>
          <button mat-raised-button color="primary"
                  [disabled]="userAccountForm.invalid"
                  matStepperNext>
            Review
            <mat-icon iconPositionEnd>arrow_forward</mat-icon>
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 3 — Review & Create                                       -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step>
        <ng-template matStepLabel>
          <span matTooltip="Account templates are reused across classes — confirm the access level is correct before saving"
                matTooltipPosition="below">Review &amp; Create</span>
        </ng-template>

        <div class="review-section">

          <!-- ── Template Details panel (starts expanded — only 2 panels total) ── -->
          <mat-expansion-panel #panelTD [expanded]="true" class="review-panel">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mat-icon class="review-panel-icon">label</mat-icon>
                Template Details
              </mat-panel-title>
              <mat-panel-description class="review-panel-desc" *ngIf="!panelTD.expanded">
                <span class="review-chip"><mat-icon class="chip-icon">label</mat-icon>{{ reviewTemplateName }}</span>
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="review-rows">
              <div class="review-row">
                <mat-icon class="review-row-icon">label</mat-icon>
                <span class="review-row-label">Name</span>
                <span class="review-row-value">{{ reviewTemplateName }}</span>
              </div>
              <div class="review-row">
                <mat-icon class="review-row-icon">description</mat-icon>
                <span class="review-row-label">Description</span>
                <span class="review-row-value">{{ reviewTemplateDesc }}</span>
              </div>
            </div>
          </mat-expansion-panel>

          <!-- ── Account Config panel (starts expanded — only 2 panels total) ── -->
          <mat-expansion-panel #panelAC [expanded]="true" class="review-panel">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mat-icon class="review-panel-icon">manage_accounts</mat-icon>
                Account Configuration
              </mat-panel-title>
              <mat-panel-description class="review-panel-desc" *ngIf="!panelAC.expanded">
                <span class="review-chip"><mat-icon class="chip-icon">cloud</mat-icon>Microsoft Azure</span>
                <span class="review-chip"><mat-icon class="chip-icon">verified_user</mat-icon>{{ reviewAzureRole }}</span>
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="review-rows">
              <div class="review-row">
                <mat-icon class="review-row-icon">cloud</mat-icon>
                <span class="review-row-label">Cloud Provider</span>
                <span class="review-row-value">Microsoft Azure</span>
              </div>
              <div class="review-row">
                <mat-icon class="review-row-icon">security</mat-icon>
                <span class="review-row-label">Access Level</span>
                <span class="review-row-value">{{ selectedAccessLevelLabel }}</span>
              </div>
              <div class="review-row">
                <mat-icon class="review-row-icon">verified_user</mat-icon>
                <span class="review-row-label">Azure Role</span>
                <span class="review-row-value">{{ reviewAzureRole }}</span>
              </div>
            </div>
          </mat-expansion-panel>

        </div>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious type="button">
            <mat-icon>arrow_back</mat-icon>
            Back
          </button>
          <button mat-raised-button color="primary"
                  [disabled]="isSubmitting"
                  (click)="onSubmit()">
            <mat-icon *ngIf="!isSubmitting">person_add</mat-icon>
            <mat-progress-spinner *ngIf="isSubmitting" mode="indeterminate" diameter="18" class="btn-spinner"></mat-progress-spinner>
            {{ isSubmitting ? 'Creating…' : 'Create Template' }}
          </button>
        </div>
      </mat-step>

    </mat-horizontal-stepper>
  </mat-card>

</div>
